/**
 * حالت یک قضاوت مقایسه‌ای و جهش‌های خط لوله.
 *
 * ⚠️ مثل مشاوره، فقط `judgment_id` سمت کلاینت می‌ماند و خودِ حالت سمت سرور است.
 * اگر کل حالت به کلاینت می‌آمد و بازمی‌گشت، کاربر می‌توانست شواهد و امتیازها را
 * دستکاری کند و قاضی روی شواهد ساختگی مقایسه می‌کرد.
 */

import { useMutation } from '@tanstack/react-query'
import { useCallback, useState } from 'react'

import { ApiError } from '@/lib/api/client'
import {
  closeJudgment,
  startJudgment,
  submitJudgmentAnswers,
  type Scope,
} from '@/lib/api/endpoints'

import type { JudgmentInput, JudgmentState } from '@/lib/types'

export interface UseJudgment {
  state: JudgmentState | null
  /** نمایه‌ی مرحله: گزینه‌ها، شفاف‌سازی، نتیجه. */
  step: number
  isBusy: boolean
  error: ApiError | null
  start: (payload: JudgmentInput) => void
  answer: (answers: (string | null)[]) => void
  reset: () => void
}

const stepOf = (state: JudgmentState | null): number => {
  if (state === null) {
    return 0
  }
  if (state.stage === 'awaiting_clarification') {
    return 1
  }
  return 2
}

export const useJudgment = (scope?: Scope): UseJudgment => {
  const [state, setState] = useState<JudgmentState | null>(null)

  const startMutation = useMutation({
    mutationFn: (payload: JudgmentInput) => startJudgment(payload, scope),
    onSuccess: setState,
  })

  const answerMutation = useMutation({
    mutationFn: (answers: (string | null)[]) => {
      if (state === null) {
        throw new ApiError({ message: 'مقایسه‌ای در جریان نیست.', status: 0 })
      }
      return submitJudgmentAnswers(state.judgment_id, answers)
    },
    onSuccess: setState,
  })

  const reset = useCallback(() => {
    const current = state
    setState(null)
    if (current !== null) {
      void closeJudgment(current.judgment_id).catch(() => {
        // بستن یک راحتی است، نه الزام: حالت خودش با TTL منقضی می‌شود.
      })
    }
  }, [state])

  const firstError = [startMutation.error, answerMutation.error].find(
    (item): item is ApiError => item instanceof ApiError,
  )

  return {
    state,
    step: stepOf(state),
    isBusy: startMutation.isPending || answerMutation.isPending,
    error: firstError ?? null,
    start: (payload: JudgmentInput) => {
      startMutation.mutate(payload)
    },
    answer: (answers: (string | null)[]) => {
      answerMutation.mutate(answers)
    },
    reset,
  }
}
